import { Box, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * KARTA — rama bloku treści, przeniesiona z `karta()` makiety (js/ui.js:106)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * DLACZEGO OSOBNY KOMPONENT, A NIE „Paper z Typography w każdym ekranie":
 * audyt parytetu wypisał tę samą różnicę przy SIEDMIU ekranach — nagłówki sekcji stały gołe na
 * kanwie jako `variant="h5"`, a treść siedziała w osobnym pudełku pod nimi. To nie jest siedem
 * usterek, tylko jedna brakująca warstwa wspólna. Naprawiona w jednym miejscu, znika wszędzie.
 *
 * CO NIESIE NADTYTUŁ — i dlaczego nie jest ozdobą:
 * makieta używa go do powiedzenia, CZEGO dotyczy blok, zanim tytuł powie, CO pokazuje
 * („CAŁY KATALOG NARAZ" nad „Portfolio dań", „13 Z 17 POZYCJI KATALOGU" nad „Bez sprzedaży").
 * Dzięki temu tytuł nie musi być zdaniem. Bez nadtytułu wracamy do nagłówków, które albo są
 * za długie, albo nie mówią nic.
 *
 * RANGA NAGŁÓWKA ≠ STYL NAGŁÓWKA. `.karta-tytul` to 16 px krojem marki — wygląd, nie miejsce
 * w hierarchii dokumentu. Ranga idzie osobno w `poziom`, domyślnie `h3`, i ma wynikać z tego,
 * co jest NAD blokiem na danym ekranie. Pomylenie tych dwóch rzeczy kosztowało już jeden
 * defekt dostępności (przeskok h2→h5 na Jakości danych).
 */

export interface KartaProps {
  children: ReactNode;
  tytul?: ReactNode;
  nadtytul?: ReactNode;
  /** Ranga nagłówka w hierarchii dokumentu — wygląd jest stały, ranga zależy od ekranu. */
  poziom?: 'h2' | 'h3' | 'h4';
  /** Cicha akcja po prawej stronie nagłówka (np. „Dowód", „Skąd ta liczba”). */
  akcja?: ReactNode;
  /** Dodatkowa klasa — używana m.in. przez `sygnal-kaskada` do wejścia sekwencyjnego. */
  className?: string;
  /** Wyściełanie w jednostkach MUI; makieta domyślnie ma `--o5` = 24 px. */
  wyscielanie?: number;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. „Wartość wg cennika" występuje
   * cztery razy na jednym ekranie, „Zamówienia w oknie" w sześciu plikach, a „przed czasem"
   * powtarza się raz na każdą szkołę w rankingu. Alternatywą było kotwiczenie po klasach MUI
   * i wartościach stylów — narzędzie, które działa dziś i zaczyna cicho kłamać po pierwszym
   * refaktorze wyglądu.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}

export function Karta({
  children,
  tytul,
  nadtytul,
  poziom = 'h3',
  akcja,
  className,
  wyscielanie = 3,
  kartaId,
}: KartaProps) {
  const maNaglowek = Boolean(tytul || nadtytul || akcja);
  return (
    <Paper variant="outlined" className={className} data-cc-card-id={kartaId} sx={{ p: wyscielanie }}>
      {maNaglowek ? (
        <Stack
          component="header"
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', mb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {nadtytul ? (
              <Typography
                sx={{
                  fontSize: 'var(--st-mikro)',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--tekst-slaby)',
                }}
              >
                {nadtytul}
              </Typography>
            ) : null}
            {tytul ? (
              <Typography
                component={poziom}
                sx={{
                  fontFamily: 'var(--krój-marka)',
                  fontWeight: 900,
                  fontSize: 'var(--st-sredni)',
                  letterSpacing: '-0.01em',
                }}
              >
                {tytul}
              </Typography>
            ) : null}
          </Box>
          {/* `rosnij` z makiety — odsuwa akcję do prawej krawędzi nagłówka. */}
          <Box sx={{ flexGrow: 1 }} />
          {akcja}
        </Stack>
      ) : null}
      {children}
    </Paper>
  );
}
